import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import './ClientUserManagement.css';

function ClientUserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullName: '',
    email: '',
    isActive: true,
    assignedRoles: []
  });

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (!user?.clientId) {
        setError('No client information available');
        return;
      }

      // Fetch users for this client
      const usersResponse = await api.get(`/admin/users/client/${user.clientId}`);
      if (usersResponse.data.success) {
        setUsers(usersResponse.data.users);
      }

      // Fetch roles for this client
      const rolesResponse = await api.get(`/admin/roles/client/${user.clientId}`);
      if (rolesResponse.data.success) {
        setRoles(rolesResponse.data.roles);
      }

      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRoleSelection = (groupId, isSelected) => {
    setFormData(prev => ({
      ...prev,
      assignedRoles: isSelected 
        ? [...(prev.assignedRoles || []), groupId]
        : (prev.assignedRoles || []).filter(id => id !== groupId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { assignedRoles, ...rest } = formData;
      const userPayload = editingUser
        ? { fullName: rest.fullName, email: rest.email, isActive: rest.isActive }
        : { ...rest, clientId: user.clientId };
      
      const response = editingUser
        ? await api.put(`/admin/users/${editingUser.UserID}`, userPayload)
        : await api.post('/admin/users', userPayload);

      if (response.data.success) {
        const createdUserId = response.data.userId || editingUser?.UserID;
        
        // Assign roles if any were selected during creation
        if (!editingUser && assignedRoles && assignedRoles.length > 0 && createdUserId) {
          for (const groupId of assignedRoles) {
            try {
              await api.post(`/admin/users/${createdUserId}/groups/${groupId}`);
            } catch (roleErr) {
              // Continue with other roles even if one fails
            }
          }
        }
        
        setSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
        resetForm();
        fetchData();
      } else {
        setError(response.data.message || 'Failed to save user');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleEdit = (userData) => {
    setEditingUser(userData);
    setFormData({
      username: userData.UserName,
      password: '',
      fullName: userData.FullName,
      email: userData.Email,
      isActive: userData.IsActive
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        const response = await api.delete(`/admin/users/${userId}`);
        if (response.data.success) {
          setSuccess('User deleted successfully');
          fetchData();
        } else {
          setError(response.data.message || 'Failed to delete user');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const handleAssignRole = async (userId, groupId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/groups/${groupId}`);
      if (response.data.success) {
        setSuccess('Role assigned successfully');
        fetchData();
        setShowRoleModal(false);
      } else {
        setError(response.data.message || 'Failed to assign role');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign role');
    }
  };

  const handleRemoveRole = async (userId, groupId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}/groups/${groupId}`);
      if (response.data.success) {
        setSuccess('Role removed successfully');
        fetchData();
      } else {
        setError(response.data.message || 'Failed to remove role');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove role');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      fullName: '',
      email: '',
      isActive: true,
      assignedRoles: []
    });
    setEditingUser(null);
    setShowCreateForm(false);
  };

  const getRoleNames = (userGroups) => {
    return userGroups?.map(g => g.GroupName).join(', ') || 'No roles assigned';
  };

  if (loading) {
    return <div className="user-management-container"><div className="loading">Loading user management...</div></div>;
  }

  return (
    <div className="user-management-container">
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="management-header">
        <h2>User Management</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '+ Add New User'}
        </button>
      </div>

      {showCreateForm && (
        <div className="form-section">
          <h3>{editingUser ? 'Edit User' : 'Create New User'}</h3>
          <form onSubmit={handleSubmit} className="user-form">
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                disabled={!!editingUser}
                required
              />
            </div>

            {!editingUser && (
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                Active
              </label>
            </div>

            {!editingUser && roles.length > 0 && (
              <div className="form-group">
                <label>Assign Roles (Optional)</label>
                <div className="roles-selection">
                  {roles.map(role => (
                    <div key={role.GroupID} className="role-checkbox">
                      <input
                        type="checkbox"
                        id={`create-role-${role.GroupID}`}
                        checked={(formData.assignedRoles || []).includes(role.GroupID)}
                        onChange={(e) => handleRoleSelection(role.GroupID, e.target.checked)}
                      />
                      <label htmlFor={`create-role-${role.GroupID}`}>
                        {role.GroupName}
                        {role.GroupDescription && <span className="role-desc"> - {role.GroupDescription}</span>}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                {editingUser ? 'Update User' : 'Create User'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-section">
        <h3>Users ({users.length})</h3>
        {users.length === 0 ? (
          <p className="no-data">No users found. Create your first user to get started.</p>
        ) : (
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.UserID}>
                    <td>{u.UserName}</td>
                    <td>{u.FullName}</td>
                    <td>{u.Email}</td>
                    <td className="roles-cell">
                      <div className="roles-display">
                        {u.groups && u.groups.length > 0 ? (
                          <>
                            <span className="role-badges">
                              {u.groups.map(g => (
                                <span key={g.GroupID} className="badge badge-role">
                                  {g.GroupName}
                                  <button
                                    className="badge-close"
                                    onClick={() => handleRemoveRole(u.UserID, g.GroupID)}
                                    title="Remove role"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </span>
                            <button
                              className="btn-role-add"
                              onClick={() => {
                                setSelectedUser(u);
                                setShowRoleModal(true);
                              }}
                            >
                              + Add Role
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn-assign-role"
                            onClick={() => {
                              setSelectedUser(u);
                              setShowRoleModal(true);
                            }}
                          >
                            Assign Role
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`status ${u.IsActive ? 'active' : 'inactive'}`}>
                        {u.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        className="btn btn-sm btn-edit"
                        onClick={() => handleEdit(u)}
                        title="Edit user"
                      >
                        ✎
                      </button>
                      <button
                        className="btn btn-sm btn-delete"
                        onClick={() => handleDelete(u.UserID)}
                        title="Delete user"
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showRoleModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h4>Assign Role to {selectedUser.UserName}</h4>
              <button
                className="modal-close"
                onClick={() => setShowRoleModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {roles.length === 0 ? (
                <p className="no-data">No roles available. Create roles first.</p>
              ) : (
                <div className="role-list">
                  {roles.map(role => {
                    const userHasRole = selectedUser.groups?.some(g => g.GroupID === role.GroupID);
                    return (
                      <div key={role.GroupID} className="role-item">
                        <input
                          type="checkbox"
                          id={`role-${role.GroupID}`}
                          checked={userHasRole}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleAssignRole(selectedUser.UserID, role.GroupID);
                            }
                          }}
                          disabled={userHasRole}
                        />
                        <label htmlFor={`role-${role.GroupID}`}>
                          <div className="role-name">{role.GroupName}</div>
                          <div className="role-desc">{role.GroupDescription}</div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientUserManagement;
